import type { RefObject } from 'react'
import { useEffect, useLayoutEffect, useState } from 'react'

type UseResizeObserverReturn = {
  width: number
  height: number
}

/**
 * Hook that observes element dimensions using ResizeObserver.
 *
 * @param ref - React ref to the element to observe
 * @returns Current width and height of the element
 */
function useResizeObserver(ref: RefObject<HTMLElement | null>): UseResizeObserverReturn {
  const [dimensions, setDimensions] = useState<UseResizeObserverReturn>({ width: 0, height: 0 })

  // Use useLayoutEffect to measure before paint, avoiding flicker
  useLayoutEffect(() => {
    if (!ref.current) return

    const element = ref.current

    // Set initial dimensions synchronously before paint
    setDimensions({
      width: element.offsetWidth,
      height: element.offsetHeight,
    })
  }, [ref])

  useEffect(() => {
    if (!ref.current) return

    const element = ref.current

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect
        setDimensions({ width, height })
      }
    })

    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [ref])

  return dimensions
}

export default useResizeObserver
